// Catalogue Jolie Fille v3 — Collection Radiant (7 références).
//
// Score morpho : ★ (1) à ★★★★★ (5), 0 si à éviter.
// Couleurs : Aura (dégradé orange-vert) · Emerald (uni) · Paprika (orange uni).
// Sizing : chaque modèle a sa propre logique (voir `sizingLogic`).

export type MorphoCode = 'A' | 'V' | 'H' | 'X' | '8' | 'O';
export type ProductCategory = 'top' | 'bottom' | 'onepiece';
export type Support = 'light' | 'medium' | 'strong';
export type Coverage = 'minimal' | 'cheeky' | 'classique' | 'couvrant';

/**
 * Stratégie de calcul de la taille recommandée par modèle.
 * - direct_top : Q4 (taille haut habituelle) directement
 * - direct_bot : Q5 (taille bas habituelle) directement
 * - palmyre_band : bande EU → taille base (70/75→XS, 80→XS, 85→S, 90→M, 95→L, 100/105→XL)
 *                  Si écart Q4 ±1 → Q11 tranche. Si écart ≥2 → taille base + alerte.
 *                  Résultat affiché en fourchette +1/+2 (S → « entre M et L »), plafonné à XL.
 * - marianne_band_cup : tableau croisé bande × bonnet (voir MARIANNE_TABLE)
 * - nicole_band_bot : bande EU → base + Q5 croisée
 */
export type SizingLogic =
  | 'direct_top'
  | 'direct_bot'
  | 'palmyre_band'
  | 'marianne_band_cup'
  | 'nicole_band_bot';

export interface ColorSwatch {
  name: string;
  bg: string;
}

export interface Product {
  id: string;
  name: string;
  type: string;
  category: ProductCategory;
  color: string;
  /** Note morpho-modèle 0-5 (5 = match parfait, 0 = à éviter, — = N/A). */
  morphos: Record<MorphoCode, number>;
  /** Niveaux de maintien matchés (bonus reco). */
  supportMatch?: Support[];
  /** Couvrances matchées (bonus reco). */
  coverageMatch?: Coverage[];
  support?: Support;
  /** Stratégie de calcul de taille. */
  sizingLogic: SizingLogic;
  sizes?: string[];
  colors?: ColorSwatch[];
  image?: string;
  productUrl?: string;
}

// Couleurs Collection Radiant
const COLORS = {
  aura: { name: 'Aura', bg: 'linear-gradient(135deg,#C8674A,#2D6A4F)' } as ColorSwatch,
  emerald: { name: 'Emerald', bg: '#2D6A4F' } as ColorSwatch,
  paprika: { name: 'Paprika', bg: '#C8674A' } as ColorSwatch,
};

const RADIANT_COLORS = [COLORS.aura, COLORS.emerald, COLORS.paprika];

// URL du shop Wix (à adapter si le client change le slug)
const SHOP = 'https://www.joliefilleswimwear.com';

// Photos Wix (récupérées via og:image, qualité augmentée à 800px)
const IMG_BASE = 'https://static.wixstatic.com/media/1da62f_';
const IMG_VARIANT = '~mv2.jpg/v1/fit/w_800,h_800,q_90/file.jpg';
const PHOTO = {
  topAdelina:  IMG_BASE + 'b93f4dc4932a4e52b1726607c1534c5c' + IMG_VARIANT,
  topPalmyre:  IMG_BASE + '4a104b0ad09c4c1da8c4e2d4961e0001' + IMG_VARIANT,
  topMarianne: IMG_BASE + '8a9e3f05f93b4eb7bde712f2afc050b1' + IMG_VARIANT,
  botAdelina:  IMG_BASE + 'f516a29ce1ad4c3381917f8fd38a4351' + IMG_VARIANT,
  botPalmyre:  IMG_BASE + 'c20d3a67422a4fad9c1d0a71098b8a0d' + IMG_VARIANT,
  botMarianne: IMG_BASE + 'ce1134d0af6c432fa049193294593e0a' + IMG_VARIANT,
  nicole:      IMG_BASE + 'f4d695403bb44f10ad5348788c8060db' + IMG_VARIANT,
};

export const JOLIE_FILLE: Product[] = [
  // ===== HAUTS =====
  {
    id: 'JF-TOP-ADELINA',
    name: 'Top Adelina',
    type: 'Triangle',
    category: 'top',
    color: '#C8674A',
    morphos: { X: 5, '8': 4, A: 5, V: 3, H: 4, O: 3 },
    supportMatch: ['light', 'medium'],
    support: 'light',
    sizingLogic: 'direct_top',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/haut-adelina`,
    image: PHOTO.topAdelina,
  },
  {
    id: 'JF-TOP-PALMYRE',
    name: 'Top Palmyre',
    type: 'Brassière',
    category: 'top',
    color: '#2D6A4F',
    morphos: { X: 3, '8': 5, A: 4, V: 3, H: 4, O: 4 },
    supportMatch: ['medium', 'strong'],
    support: 'medium',
    sizingLogic: 'palmyre_band',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/haut-palmyre`,
    image: PHOTO.topPalmyre,
  },
  {
    id: 'JF-TOP-MARIANNE',
    name: 'Top Marianne',
    type: 'Balconnet',
    category: 'top',
    color: '#B8A882',
    morphos: { X: 5, '8': 5, A: 5, V: 3, H: 4, O: 4 },
    supportMatch: ['strong'],
    support: 'strong',
    sizingLogic: 'marianne_band_cup',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/haut-marianne`,
    image: PHOTO.topMarianne,
  },

  // ===== BAS =====
  {
    id: 'JF-BOT-ADELINA',
    name: 'Bottom Adelina',
    type: 'Cheeky',
    category: 'bottom',
    color: '#C8674A',
    morphos: { X: 5, '8': 5, A: 2, V: 5, H: 4, O: 2 },
    coverageMatch: ['minimal', 'cheeky'],
    sizingLogic: 'direct_bot',
    sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/bas-adelina`,
    image: PHOTO.botAdelina,
  },
  {
    id: 'JF-BOT-PALMYRE',
    name: 'Bottom Palmyre',
    type: 'Tanga',
    category: 'bottom',
    color: '#2D6A4F',
    morphos: { X: 5, '8': 4, A: 2, V: 5, H: 4, O: 1 },
    coverageMatch: ['minimal', 'cheeky'],
    sizingLogic: 'direct_bot',
    sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/bas-palmyre`,
    image: PHOTO.botPalmyre,
  },
  {
    id: 'JF-BOT-MARIANNE',
    name: 'Bottom Marianne',
    type: 'Classique',
    category: 'bottom',
    color: '#B8A882',
    morphos: { X: 4, '8': 4, A: 4, V: 4, H: 4, O: 4 },
    coverageMatch: ['classique', 'couvrant'],
    sizingLogic: 'direct_bot',
    sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/bas-marianne`,
    image: PHOTO.botMarianne,
  },

  // ===== 1 PIÈCE =====
  {
    id: 'JF-1P-NICOLE',
    name: 'One Piece Nicole',
    type: 'Dos nu',
    category: 'onepiece',
    color: '#1A1612',
    morphos: { X: 4, '8': 4, A: 3, V: 0, H: 3, O: 3 },
    support: 'medium',
    sizingLogic: 'nicole_band_bot',
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    colors: RADIANT_COLORS,
    productUrl: `${SHOP}/article/une-pi%C3%A8ce-nicole`,
    image: PHOTO.nicole,
  },
];

// ============================================================
// Tableau croisé Top Marianne (bande × bonnet) — du slide 9 du PPT
// ============================================================
export const MARIANNE_TABLE: Record<string, Record<string, string>> = {
  '70':  { A: 'XXS', B: 'XXS', C: 'XS', D: 'XS', DD_E: 'S',  F: 'S',  G_plus: 'S'  },
  '75':  { A: 'XXS', B: 'XS',  C: 'S',  D: 'S',  DD_E: 'M',  F: 'M',  G_plus: 'M'  },
  '80':  { A: 'XS',  B: 'S',   C: 'S',  D: 'M',  DD_E: 'M',  F: 'L',  G_plus: 'L'  },
  '85':  { A: 'S',   B: 'S',   C: 'M',  D: 'M',  DD_E: 'L',  F: 'L',  G_plus: 'XL' },
  '90':  { A: 'M',   B: 'M',   C: 'L',  D: 'L',  DD_E: 'XL', F: 'XL', G_plus: 'XL' },
  '95':  { A: 'L',   B: 'L',   C: 'XL', D: 'XL', DD_E: 'XL', F: 'XL', G_plus: 'XL' },
  '100': { A: 'XL',  B: 'XL',  C: 'XL', D: 'XL', DD_E: 'XL', F: 'XL', G_plus: 'XL' },
  '105': { A: 'XL',  B: 'XL',  C: 'XL', D: 'XL', DD_E: 'XL', F: 'XL', G_plus: 'XL' },
  '110': { A: 'XL',  B: 'XL',  C: 'XL', D: 'XL', DD_E: 'XL', F: 'XL', G_plus: 'XL' },
};

// Bande EU → taille de base (Palmyre + Nicole)
export const PALMYRE_BAND_BASE: Record<string, string> = {
  '65':  'XS',
  '70':  'XS',
  '75':  'XS',
  '80':  'XS',
  '85':  'S',
  '90':  'M',
  '95':  'L',
  '100': 'XL',
  '105': 'XL',
  '110': 'XL',
};

export function getProduct(id: string): Product | undefined {
  return JOLIE_FILLE.find((p) => p.id === id);
}
